/**
 * ProfileSkillsSection — User Capability Registry on the profile About tab
 * (DEV-1378, Content Verification Stage 2).
 *
 * See SPEC_CONTENT_VERIFICATION.md §4 Stage 2 / §6 — the credibility ladder:
 *   - self-declared (no endorsements) → "discovery only" badge.
 *   - has endorsements                → "review authority" badge + per-tier counts.
 *   - SME designation                 → "SME" badge (top rung).
 *
 * OWN profile: add-skill autocomplete (canonical slugs only), remove buttons,
 * and a stubbed "Request endorsement" affordance (toast only this slice).
 * OTHER profile: view skills + an Endorse button per skill (hidden/disabled
 * when the viewer lacks standing — the API enforces the >= Silver gate too).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchUserSkills,
  addUserSkill,
  removeUserSkill,
  endorseSkill,
  type UserSkill,
  type EndorsementCountsByTier,
} from '../../api/userSkills';
import SkillPicker, { type SkillPickerSelection } from '../../Components/SkillPicker/SkillPicker';

interface ProfileSkillsSectionProps {
  /** The profile being viewed. */
  targetGuid: string;
  /** True when the viewer is looking at their own profile. */
  isOwnProfile: boolean;
}

const TIER_ORDER: Array<keyof EndorsementCountsByTier> = ['platinum', 'gold', 'silver', 'bronze'];

const sectionStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  borderRadius: 8,
  padding: 24,
  marginBottom: 20,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 16,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
};

export default function ProfileSkillsSection({ targetGuid, isOwnProfile }: ProfileSkillsSectionProps) {
  const { t } = useAppTranslation('userProfile');

  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Add-skill state (own profile only) ──
  const [busy, setBusy] = useState(false);

  // ── Transient toast (also used for the endorsement-request stub) ──
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  // ── Load the user's skills ──
  useEffect(() => {
    if (!targetGuid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchUserSkills(targetGuid)
      .then((data) => { if (!cancelled) setSkills(data); })
      .catch((err) => { if (!cancelled) setError(err?.message || t('skills.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [targetGuid, t]);

  // Currently-selected skills for the SkillPicker (own-profile add UI).
  const selectedForPicker = useMemo<SkillPickerSelection[]>(
    () => skills.map((s) => ({ skillSlug: s.slug, label: s.label })),
    [skills],
  );

  const handleAdd = useCallback(async (skill: SkillPickerSelection) => {
    if (busy) return;
    setBusy(true);
    try {
      const added = await addUserSkill(skill.skillSlug);
      setSkills((prev) => {
        const without = prev.filter((s) => s.userSkillGuid !== added.userSkillGuid);
        return [...without, added];
      });
    } catch (err: any) {
      showToast(err?.message || t('skills.addError'));
    } finally {
      setBusy(false);
    }
  }, [busy, showToast, t]);

  const handleRemove = useCallback(async (userSkillGuid: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await removeUserSkill(userSkillGuid);
      setSkills((prev) => prev.filter((s) => s.userSkillGuid !== userSkillGuid));
    } catch (err: any) {
      showToast(err?.message || t('skills.removeError'));
    } finally {
      setBusy(false);
    }
  }, [busy, showToast, t]);

  // SkillPicker removes by slug; map to the userSkillGuid the API expects.
  const handleRemoveBySlug = useCallback((slug: string) => {
    const match = skills.find((s) => s.slug.toLowerCase() === slug.toLowerCase());
    if (match) handleRemove(match.userSkillGuid);
  }, [skills, handleRemove]);

  const handleEndorse = useCallback(async (skill: UserSkill) => {
    if (busy) return;
    setBusy(true);
    try {
      const counts = await endorseSkill(targetGuid, skill.slug);
      setSkills((prev) => prev.map((s) =>
        s.slug === skill.slug
          ? { ...s, endorsementCountsByTier: counts.endorsementCountsByTier, totalEndorsements: counts.totalEndorsements }
          : s,
      ));
      showToast(t('skills.endorsedToast'));
    } catch (err: any) {
      showToast(err?.message || t('skills.endorseError'));
    } finally {
      setBusy(false);
    }
  }, [busy, targetGuid, showToast, t]);

  // ── Credibility-ladder badge for one skill ──
  const renderLadderBadge = (skill: UserSkill) => {
    let label: string;
    let color: string;
    if (skill.isSme) {
      label = t('skills.badge.sme');
      color = 'var(--accent-secondary, #a78bfa)';
    } else if (skill.totalEndorsements > 0) {
      label = t('skills.badge.reviewAuthority');
      color = 'var(--color-success)';
    } else {
      label = t('skills.badge.discoveryOnly');
      color = 'var(--text-muted)';
    }
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, color,
        border: `1px solid ${color}`, borderRadius: 4,
        padding: '2px 8px', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    );
  };

  const renderTierCounts = (skill: UserSkill) => {
    const entries = TIER_ORDER
      .map((tier) => ({ tier, n: skill.endorsementCountsByTier[tier] ?? 0 }))
      .filter((e) => e.n > 0);
    if (entries.length === 0) return null;
    return (
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {entries.map(({ tier, n }) => (
          <span key={tier} style={{
            fontSize: 11, color: 'var(--text-tertiary)',
            background: 'var(--bg-hover)', borderRadius: 4, padding: '1px 6px',
          }}>
            {t(`skills.tier.${tier}`)}: {n}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>{t('skills.sectionTitle')}</div>

      {/* ── Add-skill control (own profile only) — shared SkillPicker (design "C"). ── */}
      {isOwnProfile && (
        <div style={{ marginBottom: 16 }}>
          <SkillPicker
            selected={selectedForPicker}
            onAdd={handleAdd}
            onRemove={handleRemoveBySlug}
            disabled={busy}
          />
        </div>
      )}

      {/* ── Skills list ── */}
      {loading ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>{t('skills.loading')}</div>
      ) : error ? (
        <div style={{ color: 'var(--color-error)', fontSize: 14 }}>{error}</div>
      ) : skills.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, fontStyle: 'italic' }}>
          {isOwnProfile ? t('skills.emptyOwn') : t('skills.emptyOther')}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {skills.map((skill) => (
            <li key={skill.userSkillGuid} style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {skill.label}
              </span>
              {renderLadderBadge(skill)}
              {renderTierCounts(skill)}

              <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                {/* OWN profile: request endorsement (stub) + remove */}
                {isOwnProfile && (
                  <>
                    <button
                      type="button"
                      onClick={() => showToast(t('skills.requestEndorsementStub'))}
                      style={{
                        background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                        borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                        color: 'var(--text-secondary)', cursor: 'pointer',
                      }}
                    >
                      {t('skills.requestEndorsement')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleRemove(skill.userSkillGuid)}
                      aria-label={t('skills.remove')}
                      style={{
                        background: 'transparent', border: '1px solid var(--border-subtle)',
                        borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                        color: 'var(--text-tertiary)', cursor: 'pointer',
                      }}
                    >
                      {t('skills.remove')}
                    </button>
                  </>
                )}

                {/* OTHER profile: endorse */}
                {!isOwnProfile && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleEndorse(skill)}
                    style={{
                      background: 'var(--color-success)', border: 'none',
                      borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600,
                      color: 'var(--bg-primary)', cursor: 'pointer',
                    }}
                  >
                    {t('skills.endorse')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          marginTop: 12, fontSize: 13, color: 'var(--text-secondary)',
          background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
          borderRadius: 6, padding: '8px 12px',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
